import dayjs, { type Dayjs } from 'dayjs';
import type { FormInputType } from '../../../technical/form/Form.types.ts';
import * as yup from 'yup';
import { yupWhen } from '../../../../technical/validation.ts';
import { isNil } from 'lodash/fp';
import { parseUtcDate } from '../../../date.utils.ts';
import { DateTimeFormat, formatDate } from '../../../formatter.utils.ts';
import { isValidDayjsDateRangeWithOnlySince } from '../../../dateRange.utils.ts';

type FormOutputPortfolio = {
  id: string;
  name: string;
  extraRebalancingSelection: boolean;
};
export type FormOutputPortfolioItem = {
  portfolio: FormOutputPortfolio;
  synthPortfolioId: string | null;
};
export type FormOutputFields = {
  portfolios: FormOutputPortfolioItem[];
  portfolioLength?: unknown;
  range: [Dayjs, Dayjs] | null;
};
export type FormInputFields = FormInputType<FormOutputFields>;
export const getPortfolioId = (def: FormOutputPortfolioItem): string =>
  def.portfolio.extraRebalancingSelection ? def.synthPortfolioId! : def.portfolio.id;

export const calculateSharedPortfolioDates = (
  portfolioDates: Map<
    string,
    {
      since: UtcDate | null;
      to: UtcDate | null;
    }
  >,
  portfolios: string[]
): { firstSince: Dayjs | null; commonTo: Dayjs | null } => {
  const firstSince = dayjs.min(
    portfolios
      .map((id) => portfolioDates.get(id)?.since)
      .filter((date): date is UtcDate => !isNil(date))
      .map((date): Dayjs => {
        const parsedDate = parseUtcDate(date);
        // backend returns the dates when portfolio snapshots are available,
        // whereas filtering in the ui is based on returns, so we need to add one day to since date
        return parsedDate.add(1, 'day');
      })
  );

  const commonTo = dayjs.min(
    portfolios
      .map((id) => portfolioDates.get(id)?.to)
      .filter((date): date is UtcDate => !isNil(date))
      .map((date) => parseUtcDate(date))
  );

  return { firstSince, commonTo };
};

export const calculateSchema = (
  portfolioDates: Map<
    string,
    {
      since: UtcDate | null;
      to: UtcDate | null;
    }
  >
): yup.Schema => {
  return yup.object({
    portfolios: yup
      .array(
        yup
          .object({
            portfolio: yup.object().required(),
            synthPortfolioId: yupWhen(['portfolio'], ([portfolio]): yup.Schema => {
              if (isNil(portfolio) || !portfolio.extraRebalancingSelection) {
                return yup.mixed().nullable();
              }

              return yup.string().required();
            }),
          })
          .required()
      )
      .required(),
    portfolioLength: yup
      .mixed()
      .nullable()
      .optional()
      .when('portfolios', ([field], schema) =>
        schema.test('', 'At least one portfolio is required', () => field.length >= 1)
      ),
    range: yupWhen<[FormInputType<FormOutputPortfolioItem>[]]>(['portfolios'], ([portfolios]) => {
      const selectedIds = portfolios
        .filter((val): val is FormOutputPortfolioItem => !isNil(val) && !isNil(val.portfolio))
        .map((def) => getPortfolioId(def));
      const { firstSince, commonTo } = calculateSharedPortfolioDates(portfolioDates, selectedIds);

      return yup
        .mixed()
        .nullable()
        .test('validRange', 'Range is invalid', isValidDayjsDateRangeWithOnlySince)
        .test(
          'afterCommonStartDate',
          `Must be greater or equal to ${formatDate(firstSince, DateTimeFormat.Date)}`,
          (range: unknown): boolean => {
            if (!isValidDayjsDateRangeWithOnlySince(range)) {
              return false;
            }

            if (isNil(range)) {
              return true;
            }

            return !range[0].isBefore(firstSince);
          }
        )
        .test(
          'beforeCommonEndDateIfSet',
          `Must be lower or equal to ${formatDate(commonTo, DateTimeFormat.Date)}`,
          (range: unknown): boolean => {
            if (!isValidDayjsDateRangeWithOnlySince(range)) {
              return false;
            }

            if (isNil(range) || isNil(commonTo) || isNil(range[1])) {
              return true;
            }

            // by default component returns 'to' at the end of the day, so to compare only dates, we need to truncate time to '00:00:00'
            return !range[1].startOf('day').isAfter(commonTo);
          }
        );
    }),
  });
};
